'use client'

/**
 * Event-page "Your team" card (Apex only). A summary of the viewer's seat once
 * the organizer has generated a team map — picking, swapping and leaving all
 * happen on the full team map page, which this card points at.
 */

import { useCallback, useEffect, useState } from 'react'
import { GpBadge } from '@/components/ui/gp-badge';
import Link from 'next/link'
import { ArrowUpRight, UsersRound } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/contexts/AuthContext'
import { UserAvatar } from '@/components/ui/user-avatar'
import { getTeamMap, isTeamMapLocked, teamColor, type Team, type TeamMap } from '@/lib/tournament-teams'

export function TeamMapPlayerCard({
  tournamentId,
  startsAt,
  isRegistered,
  refreshSignal = 0,
  slug,
  locale,
}: {
  tournamentId: string
  startsAt: string
  slug: string
  locale: string
  /** Whether the viewer holds a registration (confirmed or substitute). */
  isRegistered: boolean
  /** Bump to re-fetch (e.g. after join/leave of the event). */
  refreshSignal?: number
}) {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const [map, setMap] = useState<TeamMap | null | undefined>(undefined)

  const reload = useCallback(() => getTeamMap(tournamentId).then(setMap), [tournamentId])
  useEffect(() => {
    reload()
  }, [reload, refreshSignal])

  if (!map) return null

  const locked = isTeamMapLocked(startsAt)
  const canSelfAssign = map.selfAssign && !locked && isRegistered && !!userId
  const mine = userId ? map.teams.find((t) => t.members.some((m) => m.userId === userId)) : undefined
  const openSeats = map.teams.reduce((n, t) => n + (map.teamSize - t.members.length), 0)

  return (
    <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <UsersRound className="size-4" />
          {mine ? 'Your team' : 'Teams'}
        </h3>
        <span
          className={cn(
            'rounded-full px-2 py-0.5 text-xs font-semibold',
            locked
              ? 'bg-white/10 text-white/70'
              : map.selfAssign
                ? 'bg-emerald-500/15 text-emerald-300'
                : 'bg-white/10 text-white/70'
          )}
        >
          {locked ? 'Locked' : map.selfAssign ? 'Open' : 'Assigned by organizer'}
        </span>
      </div>

      {mine ? (
        <TeamCard team={mine} teamSize={map.teamSize} userId={userId} />
      ) : (
        <p className="mt-3 text-sm text-muted-foreground">
          {!isRegistered
            ? 'Join the event to get a seat on a team.'
            : locked
              ? 'You weren’t seated before teams locked — ask the organizer.'
              : map.selfAssign
                ? `Pick your team on the full map — ${openSeats} ${openSeats === 1 ? 'seat is' : 'seats are'} still open.`
                : 'The organizer hasn’t seated you yet.'}
        </p>
      )}

      {/* Pushes the CTA to the bottom so it lines up with the leaderboard
          card's button when the two sit side by side. */}
      <div className="flex-1" />

      {/* Every action — picking, swapping, leaving — lives on the full map page. */}
      <Link
        href={`/${locale}/tournaments/${slug}/map`}
        className={cn(
          'mt-4 flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors',
          canSelfAssign && !mine
            ? 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25'
            : 'border-border bg-background hover:bg-white/5'
        )}
      >
        {canSelfAssign ? (mine ? 'Change your team' : 'Pick your team') : 'View team map'}
        <ArrowUpRight className="size-4" />
      </Link>

      {canSelfAssign && (
        <p className="mt-2 text-center text-xs text-muted-foreground">Locks 5 min before start</p>
      )}
    </div>
  )
}

function TeamCard({
  team,
  teamSize,
  userId,
}: {
  team: Team
  teamSize: number
  userId: string | null
}) {
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-border bg-white/[0.03]">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <span className="size-2 shrink-0 rounded-sm" style={{ background: teamColor(team.index) }} />
        <span className="flex-1 truncate text-sm font-semibold">{team.name}</span>
        <span className="text-xs tabular-nums text-muted-foreground">
          {team.members.length} / {teamSize}
        </span>
      </div>
      <div className="p-1">
        {Array.from({ length: teamSize }, (_, slot) => {
          const m = team.members.find((x) => x.slot === slot)
          if (!m)
            return (
              <div
                key={slot}
                className="m-1 flex h-9 items-center justify-center rounded-lg border border-dashed border-border text-xs text-muted-foreground"
              >
                Open slot
              </div>
            )
          const me = m.userId === userId
          return (
            <div
              key={slot}
              className={cn('flex h-10 items-center gap-2.5 rounded-lg px-2.5', me && 'bg-white/[0.04]')}
            >
              <UserAvatar url={m.avatar} alt={m.gamertag} size={26} />
              <span className="flex min-w-0 flex-1 items-center gap-1.5">
                <span className="truncate text-sm font-medium">{m.gamertag}</span>
                <GpBadge userId={m.userId} gamertag={m.gamertag} size={13} />
              </span>
              {me && (
                <span className="rounded-full bg-white/10 px-1.5 py-px text-[10px] font-semibold text-white/70">
                  You
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
